import React, {useEffect, useMemo, useRef, useState} from "react";
import {Button} from "@progress/kendo-react-buttons";
import {ADMIN_ACCOUNT_SSO_PROVIDER_API, ID_PLACE_HOLDER, SUCCESS_STATUS} from "../../../api/constants";
import {semcastAxios, showErrorToastMessage, showSuccessToastMessage} from "../../../Utils/Common";
import {
    buildAccountSsoProviderPayload,
    buildCustomerSsoUrl,
    isDiscoveryUrlOnlyProvider,
    providerToForm
} from "../../../Utils/AccountSsoProvider";

const emptyForm = providerToForm(null);

const buildEndpoint = (foundingUserId) => {
    return ADMIN_ACCOUNT_SSO_PROVIDER_API.replace(ID_PLACE_HOLDER, foundingUserId);
};

const getResponseMessage = (error) => {
    const data = error?.response?.data?.data;
    return typeof data === "string" ? data : "";
};

export const saveErrorMessage = (error) => {
    const message = getResponseMessage(error);

    if (error?.response?.status === 401) {
        return "You are not authorized to manage SSO provider configuration";
    }
    if (message.indexOf("Secure client_secret storage is not configured") >= 0) {
        return "SSO secrets cannot be saved until secure client-secret storage is configured.";
    }
    if (message.indexOf("login_slug is already in use") >= 0) {
        return "Login slug is already in use";
    }
    if (message.indexOf("login_slug") >= 0) {
        return "Login slug may contain only letters, numbers, and hyphens";
    }
    if (message.indexOf("tenant_id") >= 0 || message.indexOf("discovery_url") >= 0) {
        return "Tenant ID is required for Microsoft Entra SSO";
    }
    if (message.indexOf("client_id") >= 0) {
        return "Client ID is required";
    }
    if (message.indexOf("client_secret") >= 0) {
        return "Client secret is required";
    }

    return "Unable to save SSO provider configuration";
};

export default function AccountSsoProviderSetup({foundingUserId, requiredSsoLoginType, isEditable, endpoint: endpointOverride}) {
    const [provider, setProvider] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [loading, setLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [loadedEndpoint, setLoadedEndpoint] = useState("");
    const activeRequestRef = useRef({id: 0, endpoint: ""});

    const providerExists = Boolean(provider?.id);
    const clientSecretConfigured = Boolean(provider?.client_secret_configured);
    const discoveryUrlOnly = isDiscoveryUrlOnlyProvider(provider);
    const endpoint = useMemo(
        () => endpointOverride || (foundingUserId ? buildEndpoint(foundingUserId) : ""),
        [endpointOverride, foundingUserId]
    );
    const browserOrigin = typeof window !== "undefined" ? window.location.origin : "";
    const clipboardAvailable = typeof navigator !== "undefined" && Boolean(navigator.clipboard);
    const customerSsoUrl = buildCustomerSsoUrl(browserOrigin, provider?.login_slug);
    const microsoftRequired = requiredSsoLoginType === "M";
    const contextLoading = loading || loadedEndpoint !== endpoint;
    const controlsDisabled = !isEditable || contextLoading || saving || loadFailed;
    const actionDisabled = contextLoading || saving || loadFailed;
    const showProviderWarning = !loadFailed && microsoftRequired && (!providerExists || !provider?.enabled);

    const isCurrentEndpoint = (requestEndpoint) => {
        return activeRequestRef.current.endpoint === requestEndpoint;
    };

    const setField = (field, value) => {
        if (controlsDisabled) {
            return;
        }
        setForm((prev) => ({...prev, [field]: value}));
        setErrors((prev) => ({...prev, [field]: ""}));
    };

    const resetFromProvider = (nextProvider, nextEndpoint = endpoint) => {
        setProvider(nextProvider);
        setForm(providerToForm(nextProvider));
        setErrors({});
        setLoadFailed(false);
        setLoadedEndpoint(nextEndpoint);
    };

    useEffect(() => {
        const requestEndpoint = endpoint;
        const requestId = activeRequestRef.current.id + 1;
        activeRequestRef.current = {id: requestId, endpoint: requestEndpoint};

        setProvider(null);
        setForm(emptyForm);
        setErrors({});
        setLoadFailed(false);
        setLoadedEndpoint("");
        setSaving(false);

        if (!requestEndpoint) {
            setLoading(false);
            return;
        }

        const isCurrentRequest = () => {
            return activeRequestRef.current.id === requestId
                && activeRequestRef.current.endpoint === requestEndpoint;
        };

        setLoading(true);
        semcastAxios.get(requestEndpoint, {withCredentials: true})
            .then((res) => {
                if (!isCurrentRequest()) {
                    return;
                }
                if (res?.data?.status === SUCCESS_STATUS) {
                    resetFromProvider(res.data.data, requestEndpoint);
                }
            })
            .catch((error) => {
                if (!isCurrentRequest()) {
                    return;
                }
                if (error?.response?.status === 404) {
                    resetFromProvider(null, requestEndpoint);
                } else {
                    setProvider(null);
                    setForm(emptyForm);
                    setErrors({});
                    setLoadFailed(true);
                    setLoadedEndpoint(requestEndpoint);
                    showErrorToastMessage("Unable to load SSO provider configuration", 8);
                }
            })
            .finally(() => {
                if (isCurrentRequest()) {
                    setLoading(false);
                }
            });
    }, [endpoint]);

    const validate = () => {
        const nextErrors = {};
        if (!form.loginSlug.trim()) {
            nextErrors.loginSlug = "Login slug is required.";
        }
        if (!discoveryUrlOnly && !form.tenantId.trim()) {
            nextErrors.tenantId = "Tenant ID is required.";
        }
        if (!form.clientId.trim()) {
            nextErrors.clientId = "Client ID is required.";
        }
        if (!providerExists && !form.clientSecret.trim()) {
            nextErrors.clientSecret = "Client secret is required.";
        }
        if (providerExists && form.enabled && !clientSecretConfigured && !form.clientSecret.trim()) {
            nextErrors.clientSecret = "Client secret is required before enabling.";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const saveProvider = async () => {
        if (actionDisabled) {
            return;
        }
        const requestEndpoint = endpoint;
        if (!validate()) {
            showErrorToastMessage("Please correct the SSO provider fields", 6);
            return;
        }

        const payload = buildAccountSsoProviderPayload({
            form,
            includeTenantId: !discoveryUrlOnly
        });

        setSaving(true);
        try {
            const res = await semcastAxios.put(requestEndpoint, payload, {withCredentials: true});
            if (!isCurrentEndpoint(requestEndpoint)) {
                return;
            }
            if (res?.data?.status === SUCCESS_STATUS) {
                resetFromProvider(res.data.data, requestEndpoint);
                showSuccessToastMessage("SSO provider configuration saved");
            } else {
                showErrorToastMessage("Unable to save SSO provider configuration", 8);
            }
        } catch (error) {
            if (isCurrentEndpoint(requestEndpoint)) {
                showErrorToastMessage(saveErrorMessage(error), 8);
            }
        } finally {
            if (isCurrentEndpoint(requestEndpoint)) {
                setSaving(false);
            }
        }
    };

    const disableProvider = async () => {
        if (actionDisabled || !providerExists || !provider.enabled) {
            return;
        }
        if (microsoftRequired && !window.confirm("Microsoft SSO is currently required for this account. Disable the account-backed Microsoft provider?")) {
            return;
        }

        const requestEndpoint = endpoint;
        setSaving(true);
        try {
            const res = await semcastAxios.put(`${requestEndpoint}/disable`, {}, {withCredentials: true});
            if (!isCurrentEndpoint(requestEndpoint)) {
                return;
            }
            if (res?.data?.status === SUCCESS_STATUS) {
                resetFromProvider(res.data.data, requestEndpoint);
                showSuccessToastMessage("SSO provider disabled");
            } else {
                showErrorToastMessage("Unable to disable SSO provider configuration", 8);
            }
        } catch (error) {
            if (isCurrentEndpoint(requestEndpoint)) {
                showErrorToastMessage("Unable to disable SSO provider configuration", 8);
            }
        } finally {
            if (isCurrentEndpoint(requestEndpoint)) {
                setSaving(false);
            }
        }
    };

    const copyLoginUrl = async () => {
        if (!customerSsoUrl || !clipboardAvailable) {
            return;
        }

        try {
            await navigator.clipboard.writeText(customerSsoUrl);
            showSuccessToastMessage("Customer SSO URL copied");
        } catch (error) {
            showErrorToastMessage("Unable to copy customer SSO URL", 6);
        }
    };

    if (!endpoint) {
        return null;
    }

    return (
        <div
            className="section-card section-card-inner-margins account-sso-provider-section"
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                }
            }}
        >
            <div className="card-body">
                <div className="card-title">Microsoft Entra Account SSO</div>
                {contextLoading ? (
                    <div className="account-sso-status">Loading SSO provider configuration...</div>
                ) : (
                    <>
                        {loadFailed && (
                            <div className="account-sso-status">SSO provider configuration is unavailable.</div>
                        )}
                        {showProviderWarning && (
                            <div className="account-sso-warning">
                                Microsoft SSO is required for this account, but the account-backed Microsoft provider is not currently enabled.
                            </div>
                        )}
                        {!loadFailed && !providerExists && (
                            <div className="account-sso-status">Account SSO provider not configured</div>
                        )}
                        {providerExists && !provider.enabled && (
                            <div className="account-sso-status">Provider disabled</div>
                        )}
                        {discoveryUrlOnly && (
                            <div className="account-sso-status">
                                This provider currently uses an existing discovery URL configuration.
                            </div>
                        )}
                        <div className="col-md-12 mt-3 p-0 mb-3">
                            <div className="row">
                                <div className="col-4">
                                    <div className="form-group">
                                        <label className="form-label">Provider</label>
                                        <input
                                            type="text"
                                            className="form-control input-rounded"
                                            value="Microsoft Entra"
                                            disabled
                                            readOnly
                                        />
                                    </div>
                                </div>
                                <div className="col-4">
                                    <div className="form-group account-sso-enabled-field">
                                        <label className="form-label">Enabled</label>
                                        <input
                                            type="checkbox"
                                            checked={form.enabled}
                                            disabled={controlsDisabled}
                                            onChange={(e) => setField("enabled", e.target.checked)}
                                        />
                                    </div>
                                </div>
                                <div className="col-4">
                                    <div className="form-group">
                                        <label className="form-label">Display Name</label>
                                        <input
                                            type="text"
                                            className="form-control input-rounded"
                                            value={form.displayName}
                                            disabled={controlsDisabled}
                                            onChange={(e) => setField("displayName", e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row account-sso-row">
                                <div className="col-4">
                                    <div className="form-group">
                                        <label className="form-label required-asterisk">Login Slug</label>
                                        <input
                                            type="text"
                                            className="form-control input-rounded"
                                            value={form.loginSlug}
                                            disabled={controlsDisabled}
                                            onChange={(e) => setField("loginSlug", e.target.value)}
                                        />
                                        <span className="account-sso-help">Used in the customer SSO URL.</span>
                                        <span className="validation-error">{errors.loginSlug || null}</span>
                                    </div>
                                </div>
                                <div className="col-4">
                                    <div className="form-group">
                                        <label className="form-label required-asterisk">Tenant ID</label>
                                        <input
                                            type="text"
                                            className="form-control input-rounded"
                                            value={form.tenantId}
                                            disabled={controlsDisabled || discoveryUrlOnly}
                                            onChange={(e) => setField("tenantId", e.target.value)}
                                        />
                                        <span className="validation-error">{errors.tenantId || null}</span>
                                    </div>
                                </div>
                                <div className="col-4">
                                    <div className="form-group">
                                        <label className="form-label required-asterisk">Client ID</label>
                                        <input
                                            type="text"
                                            className="form-control input-rounded"
                                            value={form.clientId}
                                            disabled={controlsDisabled}
                                            onChange={(e) => setField("clientId", e.target.value)}
                                        />
                                        <span className="validation-error">{errors.clientId || null}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="row account-sso-row">
                                <div className="col-4">
                                    <div className="form-group">
                                        <label className={`form-label ${!providerExists || !clientSecretConfigured ? "required-asterisk" : ""}`}>Client Secret</label>
                                        <input
                                            type="password"
                                            className="form-control input-rounded"
                                            value={form.clientSecret}
                                            disabled={controlsDisabled}
                                            autoComplete="new-password"
                                            onChange={(e) => setField("clientSecret", e.target.value)}
                                        />
                                        {providerExists && clientSecretConfigured && (
                                            <span className="account-sso-help">Leave blank to keep the current secret.</span>
                                        )}
                                        <span className="validation-error">{errors.clientSecret || null}</span>
                                    </div>
                                </div>
                                <div className="col-8">
                                    <div className="form-group">
                                        <label className="form-label">Customer SSO URL</label>
                                        <div className="account-sso-url-row">
                                            <input
                                                type="text"
                                                className="form-control input-rounded account-sso-url"
                                                value={customerSsoUrl}
                                                readOnly
                                                placeholder="Save a login slug to generate the customer SSO URL"
                                            />
                                            <Button
                                                type="button"
                                                className="button-standard button-submit"
                                        disabled={!customerSsoUrl || !clipboardAvailable}
                                                onClick={copyLoginUrl}
                                            >
                                                Copy
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {isEditable && (
                            <div className="account-sso-actions">
                                {providerExists && provider.enabled && (
                                    <Button
                                        type="button"
                                        className="button-standard button-white"
                                        disabled={actionDisabled}
                                        onClick={disableProvider}
                                    >
                                        Disable Provider
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    className="button-standard button-submit"
                                    disabled={actionDisabled}
                                    onClick={saveProvider}
                                >
                                    Save SSO Provider
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
